import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { OverlayTrigger, Tooltip } from "react-bootstrap";
import DataTable from "react-data-table-component";
import * as icons from "react-bootstrap-icons";
import { toast } from "react-toastify";
import TopNavbar from "../../CommonComponent/topNavbar/TopNavbar";
import Sidebar from "../../CommonComponent/sidebar/Sidebar";
import { Footer } from "../../Footer";
import { GetBroadcastById } from "../../redux/auth-reducer/auth-service";
import { readApiMessage } from "../../utils/helper/helper";
import {
  BROADCASTS_PATH,
  DETAILS_LOAD_FAILED_MESSAGE,
  GENERAL_FAILURE_ALERT,
  PARTIAL_STOP_ALERT,
  PROGRESS_POLL_MS,
  STOPPED_UPDATING_MESSAGE,
  formatSentAt,
  generalFailure,
  isInProgress,
  isServerFailure,
  partialStop,
  playersLabel,
  recipientStatusView,
  skippedLabel,
} from "../../utils/helper/broadcasts";
import { BroadcastAlert } from "./BroadcastAlert.jsx";

/**
 * A recipient's status: a green Sent, a grey Sending…, or a red Failed with the reason on hover and
 * on keyboard focus.
 */
const RecipientStatus = ({ recipient }) => {
  const view = recipientStatusView(recipient.status);

  if (view.kind !== "failed") {
    return <span className={`broadcast-status ${view.kind}`}>{view.label}</span>;
  }

  return (
    <span className="broadcast-status-wrap">
      <span className="broadcast-status failed">{view.label}</span>
      <OverlayTrigger
        placement="top"
        overlay={<Tooltip id={`broadcast-error-${recipient.recipientId}`}>{recipient.errorMessage || "The email could not be sent."}</Tooltip>}
      >
        <button type="button" className="broadcast-status-info" aria-label={recipient.errorMessage || "Why it failed"}>
          <icons.ExclamationCircleFill aria-hidden="true" />
        </button>
      </OverlayTrigger>
    </span>
  );
};

/**
 * MG-014 / AD-013 (#167, #168): Broadcast Details - when the broadcast was sent and as whom, its
 * subject and message, and every recipient with what happened to their email. While the background
 * sender is still working through it, the page reads it again every few seconds, and stops at the
 * first read that gets no answer (no reply or a 5xx), saying so.
 */
export const BroadcastDetails = () => {
  const { id } = useParams();
  const [data, setData] = useState(false);
  const [details, setDetails] = useState(null);
  const [notFound, setNotFound] = useState(false);
  const [loading, setLoading] = useState(true);
  const [pollFailed, setPollFailed] = useState(false);
  const [pollKey, setPollKey] = useState(0);

  function sidebarfun() {
    setData(!data);
  }

  useEffect(() => {
    let current = true;
    GetBroadcastById(id).then((res) => {
      if (!current) return;
      setLoading(false);
      if (res?.data?.status) {
        setDetails(res.data);
        setPollFailed(false);
      } else if (isServerFailure(res)) {
        // No reply or a 5xx, whose ErrorDetails body is not an answer about the broadcast: apiClient
        // has already said so. Stop polling; what is shown stays.
        setPollFailed(true);
      } else {
        setNotFound(true);
        if (res.status >= 400) toast.error(readApiMessage(res));
      }
    });
    return () => {
      current = false;
    };
  }, [id, pollKey]);

  const inProgress = isInProgress(details);

  useEffect(() => {
    if (!inProgress || pollFailed) return undefined;
    const timer = setTimeout(() => setPollKey((key) => key + 1), PROGRESS_POLL_MS);
    return () => clearTimeout(timer);
  }, [inProgress, pollFailed, details]);

  const columns = [
    {
      name: <strong>Player Name</strong>,
      selector: (row) => row?.playerName,
      wrap: true,
    },
    {
      name: <strong>Email</strong>,
      selector: (row) => row?.emailId || "—",
      wrap: true,
    },
    {
      name: <strong>Status</strong>,
      cell: (row) => <RecipientStatus recipient={row} />,
      minWidth: "160px",
    },
  ];

  const skipped = skippedLabel(details?.skippedCount);

  return (
    <>
      <TopNavbar sidebarfun={sidebarfun} />
      <div className="dasboard-group">
        <Sidebar data={data} />
        <div className={data ? "dashboard-content full-contnet" : "dashboard-content"}>
          <Link to={BROADCASTS_PATH} className="page-back">
            <icons.ArrowLeft aria-hidden="true" />
            Broadcasting Center
          </Link>
          <h1 className="heading-dash page-heading">Broadcast Details</h1>

          <div className="group_data_table broadcast-card">
            {loading && <div className="broadcast-loading">Loading…</div>}

            {pollFailed && !details && (
              <BroadcastAlert variant="danger" role="alert">
                {DETAILS_LOAD_FAILED_MESSAGE}
              </BroadcastAlert>
            )}

            {notFound && (
              <div className="broadcast-empty">
                <icons.EnvelopeExclamation className="broadcast-empty-icon" aria-hidden="true" />
                <p>This broadcast could not be found.</p>
                <Link to={BROADCASTS_PATH} className="broadcast-btn broadcast-btn-ghost">
                  Back to Broadcasting Center
                </Link>
              </div>
            )}

            {details && (
              <>
                {pollFailed && (
                  <BroadcastAlert variant="warning" role="status">
                    {STOPPED_UPDATING_MESSAGE}
                  </BroadcastAlert>
                )}
                {generalFailure(details) && (
                  <BroadcastAlert variant="danger" role="alert" title={GENERAL_FAILURE_ALERT.title}>
                    {GENERAL_FAILURE_ALERT.text}
                  </BroadcastAlert>
                )}
                {partialStop(details) && (
                  <BroadcastAlert variant="warning" role="alert" title={PARTIAL_STOP_ALERT.title}>
                    {PARTIAL_STOP_ALERT.text}
                  </BroadcastAlert>
                )}

                <div className="broadcast-grid">
                  <div className="broadcast-cell">
                    <small>Date and time sent</small>
                    <b>{formatSentAt(details.sentAt)}</b>
                  </div>
                  <div className="broadcast-cell">
                    <small>Sent as</small>
                    <b>{details.senderDisplayName}</b>
                  </div>
                  <div className="broadcast-cell">
                    <small>Total recipients</small>
                    <b>{playersLabel(details.recipientCount)}</b>
                    {skipped && <span className="broadcast-cell-sub">{skipped}</span>}
                  </div>
                </div>

                <div className="broadcast-section">Subject</div>
                <div className="broadcast-subject">{details.subject}</div>

                <div className="broadcast-section">Message</div>
                <div className="broadcast-message">{details.body}</div>

                <div className="broadcast-section">
                  Recipients <span className="broadcast-pill">{details.recipientCount}</span>
                </div>
                <DataTable
                  columns={columns}
                  data={details.recipients || []}
                  keyField="recipientId"
                  persistTableHead
                  noDataComponent={<div className="broadcast-empty">No recipients.</div>}
                />
              </>
            )}
          </div>
        </div>
        <div className={data ? "footer" : "footer-content"}>
          <Footer />
        </div>
      </div>
    </>
  );
};
